import { env } from '@/env';
import mixpanel, { Callback, Dict, RequestOptions } from 'mixpanel-browser';

// Product identifier for filtering in Mixpanel.
// Do NOT use the key `source` — that is reserved for CTA origin per Access taxonomy
// (welcome, header, medicare_coverage, gating stage, …).
const APP = 'access-registration';

// Pre-RND-37449 super-property keys (source/appVersion/userId). Persisted in the
// Mixpanel cookie for anyone who loaded an older bundle, so they are unregistered
// on init — see the comment at the register() call below.
const LEGACY_SUPER_PROPERTIES = ['source', 'appVersion', 'userId'] as const;

const MIXPANEL_DEBUG = env.MIXPANEL_DEBUG === 'true';

class AnalyticsManager {
  isInitialized = false;

  constructor() {
    if (env.MIXPANEL_TOKEN) {
      mixpanel.init(env.MIXPANEL_TOKEN, {
        api_host: env.MIXPANEL_API_HOST,
        debug: MIXPANEL_DEBUG,
        ignore_dnt: true,
        cookie_domain: window.location.hostname,
        cross_subdomain_cookie: false,
      });

      // mixpanel.register is additive and persists in the cookie/localStorage —
      // only reset() clears super properties wholesale. A returning visitor who
      // loaded a pre-rename bundle therefore still carries the legacy keys, and
      // a stale `source: 'access-registration'` would override the CTA-origin
      // `source` on every event that doesn't set one. Drop them explicitly.
      LEGACY_SUPER_PROPERTIES.forEach((key) => mixpanel.unregister(key));

      this.registerBaseSuperProperties();

      this.isInitialized = true;
    }
  }

  // Super properties included on every event. Extracted so reset() can restore
  // them — mixpanel.reset() clears super properties wholesale, and `app` is the
  // key every board filters on, so losing it would be silent and total. No
  // application code calls reset() today; a start-over flow is the obvious
  // future caller (PR #101 review).
  private registerBaseSuperProperties() {
    mixpanel.register({
      app: APP,
      app_version: env.APP_VERSION,
      environment: env.MODE,
    });
  }

  setUserProperties(properties: Dict) {
    if (this.isInitialized) {
      mixpanel.register_once(properties);
    }
  }

  identifyUser(analyticsID: string) {
    if (this.isInitialized && analyticsID) {
      mixpanel.identify(analyticsID);
    }
  }

  track(
    eventName: string,
    properties: Dict = {},
    options: RequestOptions = {},
    callback: Callback = () => {},
  ) {
    if (this.isInitialized) {
      mixpanel.track(eventName, properties, options, callback);
    } else if (env.IS_DEV) {
      console.log('[Analytics] Track:', eventName, '(properties hidden)');
    }
  }

  trackError(eventName: string, properties: Dict = {}) {
    this.track(eventName, { ...properties, is_error: true });
  }

  reset() {
    if (this.isInitialized) {
      mixpanel.reset();
      this.registerBaseSuperProperties();
    }
  }
}

const analytics = new AnalyticsManager();

export default analytics;
